/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { userHasOneOfTheseRoles } from "../../../../services/backofficeUserService";
import { Roles } from "../../../../types/BackofficeUser";
import { useCurrentUser } from "../../../../provider/CurrentUserProvider";
import ContentBox from "../../../ContentBox/ContentBox";
import { Box, Typography } from "@mui/material";
import ButtonLoading from "../../../Button/ButtonLoading";
import React, { SyntheticEvent, useState } from "react";
import ApiClient from "../../../../services/ApiClient";
import Grid from "@mui/material/Grid";
import ValidatorSelect from "../../../Validator/ValidatorSelect";
import MenuItem from "@mui/material/MenuItem";
import SearchBar from "../../../Algolia/SearchBar";
import { ALGOLIA_INDEX } from "../../../../config/_entrypoint";
import CaseHit from "../../../Case/CaseEntityPicker/Hit/CaseHit";
import { CaseHit as CaseHitType } from "../../../Case/CaseEntityPicker/Hit/hitType";
import ValidatorTextField from "../../../Validator/ValidatorTextField";
import { MediaObject } from "../../../../types/MediaObject";

export default function AiLabOcr() {
  const currentUser = useCurrentUser();
  const [mediaObjects, setMediaObjects] = useState<Array<MediaObject>>([]);
  const [mediaObjectId, setMediaObjectId] = useState(0);
  const [generatedText, setGeneratedText] = useState("");
  const [generatedTextRaw, setGeneratedTextRaw] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const fetchMediaObjects = async (product: string, productId: number) => {
    setIsLoading(true);
    try {
      const data = await ApiClient.get(`/media_objects?product=${product}&productId=${productId}`);

      const items = (data?.["hydra:member"] ?? [])
        .map((m: any) => ({ id: m.id, originalName: m.originalName }))
        .filter((m: any) => m.id != null && m.originalName)
        .sort((a: MediaObject, b: MediaObject) =>
          a.originalName.localeCompare(b.originalName, "de", { numeric: true, sensitivity: "base" })
        );
      setMediaObjects(items);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const onHitClick = async ({ product, productId }: CaseHitType, event?: SyntheticEvent<HTMLElement>) => {
    if (!event) {
      return;
    }
    event.preventDefault();

    await fetchMediaObjects(product, productId);
  };

  const sendOcrRequest = async () => {
    setIsLoading(true);

    const response = await ApiClient.post(`/lb/services/ai_lab/send_ocr_request`, {
      body: JSON.stringify({
        mediaObjectId: mediaObjectId,
      }),
    });

    setGeneratedText(response["generatedText"] || "");
    setGeneratedTextRaw(response["generatedTextRaw"] || "");

    setIsLoading(false);
  };

  if (!userHasOneOfTheseRoles(currentUser, [Roles.admin, Roles.developer, Roles.productManager])) {
    return <></>;
  }

  return (
    <ContentBox headline="🔎📄 OCR">
      <Grid container spacing={1} sx={{
        alignItems: "center"
      }}>
        <Grid size={12}>
          <Typography>
            Hier können MediaObjects von Cases zum Testen dem OCR Prozess zugeführt werden. Da hierbei Kosten auf
            Seitenbasis anfallen, sind die Seiten, welche verarbeitet werden auf maximal 50 Seiten begrenzt.
          </Typography>
        </Grid>
        <Grid size={6}>
          <SearchBar
            searchConfig={{
              indexName: ALGOLIA_INDEX,
            }}
            resultListConfig={{
              itemSize: 330,
              width: "100%",
            }}
            HitComponent={CaseHit}
            onHitClick={onHitClick}
          />
        </Grid>
        <Grid size={6}>
          <ValidatorSelect
            label="MediaObjects"
            value={mediaObjectId}
            onChange={({ target }: any) => setMediaObjectId(target.value)}
          >
            {mediaObjects.map(({ id, originalName }) => (
              <MenuItem key={id} value={id}>
                {originalName}
              </MenuItem>
            ))}
          </ValidatorSelect>
        </Grid>
        <Grid
          sx={{
            py: 3
          }}
          size={12}>
          <ButtonLoading
            variant={"contained"}
            onClick={sendOcrRequest}
            isLoading={isLoading}
            disabled={isLoading || !mediaObjectId}
          >
            OCR Prozess starten
          </ButtonLoading>
        </Grid>
        {generatedText && (
          <>
            <Grid size={12}>
              <Typography variant="body1">
                <strong>Generierter Text:</strong>
              </Typography>
              <Box
                sx={{
                  height: 300,
                  overflowY: "auto",
                  border: "1px solid",
                  borderColor: "grey.400",
                  p: 1,
                }}
              >
                <div dangerouslySetInnerHTML={{ __html: generatedText }} />
              </Box>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                label={"Raw Markdown Output"}
                name={"generatedTextRaw"}
                value={generatedTextRaw}
                multiline
                rows={8}
              />
            </Grid>
          </>
        )}
      </Grid>
    </ContentBox>
  );
}
